import React, { useState } from "react";

const EMAIL = "hmtechnologies.work@gmail.com";
// Submissions go to /api/contact, a Vercel serverless function (api/contact.js)
// that sends the email through Resend. It needs RESEND_API_KEY set in the
// Vercel project. Set REACT_APP_FORM_ENDPOINT to use a form service instead.
// If the endpoint is missing, unconfigured, or not JSON (the local dev server
// has no functions), the form falls back to opening the visitor's mail client.
const ENDPOINT = process.env.REACT_APP_FORM_ENDPOINT || "/api/contact";

const PROJECT_TYPES = [
  "Mobile app (iOS & Android)",
  "Web app or admin dashboard",
  "Website",
  "Fix or extend an existing app",
  "Something else",
];

const inputCls =
  "w-full rounded-xl bg-white/[0.04] border border-white/10 px-4 py-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-aurora-teal/60 focus:bg-white/[0.06]";

const ContactForm = ({ compact = false }) => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    type: PROJECT_TYPES[0],
    message: "",
    company: "", // honeypot: real users never see or fill this
  });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | sent-mailto | error
  const [error, setError] = useState("");

  const update = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.company) return; // bot
    const subject = `Project enquiry: ${form.type}`;
    const body = `Name: ${form.name}\nEmail: ${form.email}\nProject: ${form.type}\n\n${form.message}`;
    const openMailClient = () => {
      window.location.href = `mailto:${EMAIL}?subject=${encodeURIComponent(
        subject
      )}&body=${encodeURIComponent(body)}`;
      setStatus("sent-mailto");
    };

    try {
      setStatus("sending");
      const res = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          project: form.type,
          message: form.message,
          company: form.company,
          _subject: subject,
        }),
      });
      const isJson = (res.headers.get("content-type") || "").includes("application/json");
      if (!isJson || res.status === 404) {
        // No server-side handler here (static preview or dev server).
        openMailClient();
        return;
      }
      const data = await res.json();
      if (data.error === "not_configured") {
        console.warn(
          "Contact form: RESEND_API_KEY is not set on the server, opening the mail client instead."
        );
        openMailClient();
        return;
      }
      if (!res.ok || data.ok === false) {
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      setStatus("sent");
    } catch (err) {
      console.warn("Contact form submit failed:", err);
      setError(err.message || "");
      setStatus("error");
    }
  };

  if (status === "sent" || status === "sent-mailto") {
    return (
      <div className="glass rounded-2xl p-6 md:p-8 text-center">
        <div className="text-3xl mb-3">✓</div>
        <h4 className="font-jamjuree text-xl font-semibold text-white">
          {status === "sent" ? "Thanks, your message is in." : "Almost there."}
        </h4>
        <p className="mt-2 text-sm text-white/65">
          {status === "sent"
            ? "We reply within 24 hours, usually sooner."
            : "Your mail app should have opened with the details filled in. If it didn't, email us at " +
              EMAIL +
              "."}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      className={`glass rounded-2xl ${compact ? "p-5 md:p-6" : "p-6 md:p-8"} space-y-4`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="sr-only">Your name</span>
          <input
            name="name"
            value={form.name}
            onChange={update}
            required
            autoComplete="name"
            placeholder="Your name"
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className="sr-only">Email</span>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={update}
            required
            autoComplete="email"
            placeholder="Email"
            className={inputCls}
          />
        </label>
      </div>

      <label className="block relative">
        <span className="sr-only">Project type</span>
        <select
          name="type"
          value={form.type}
          onChange={update}
          className={`${inputCls} appearance-none bg-ink-900 pr-10`}
        >
          {PROJECT_TYPES.map((t) => (
            <option key={t} value={t} className="bg-ink-900 text-white">
              {t}
            </option>
          ))}
        </select>
        <svg
          className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-white/50"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </label>

      <label className="block">
        <span className="sr-only">Message</span>
        <textarea
          name="message"
          value={form.message}
          onChange={update}
          required
          rows={compact ? 4 : 5}
          placeholder="What are you building, and when do you need it?"
          className={`${inputCls} resize-y`}
        />
      </label>

      {/* honeypot */}
      <input
        type="text"
        name="company"
        value={form.company}
        onChange={update}
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />

      {status === "error" && (
        <p className="text-sm text-aurora-rose">
          {error ? `${error} ` : "Sending failed. "}
          You can also email us at {EMAIL}.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-4 pt-1">
        <button
          type="submit"
          disabled={status === "sending"}
          className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === "sending" ? "Sending…" : "Send message"}
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path
              d="M5 12h14M13 5l7 7-7 7"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <span className="text-xs text-white/45">
          We reply within 24 hours.
        </span>
      </div>
    </form>
  );
};

export default ContactForm;
